<template>
  <v-row class="flex-grow-1 fill-height overflow-auto">
    <v-col cols="6" class="fill-height overflow-auto">
      <div class="d-flex pb-2 justify-space-between">
        <h2 class="navy--text">Tasks</h2>
        <v-btn @click="showModal = true" rounded color="secondary font-weight-black">Create</v-btn>
      </div>
      <v-card class="overflow-auto" elevation="0">
        <div v-if="userTasks.length == 0" class="text-h6 ml-5 mt-3 grey-navy--text">
          No tasks
        </div>

        <div v-for="taskEvent in userTasks" :key="taskEvent.node.id" class="transparent">
          <task-event-listing-card
            :task-event="taskEvent.node"
            :selected-event="selectedEvent"
            @select="selectTask($event)"
          />
        </div>
      </v-card>
      <template v-if="chatsForUser && chatsForUser.length > 0">
        <div class="d-flex justify-space-between pt-6">
          <h2 class="primary--text">Chats</h2>
          <v-btn v-if="!hasCoachingChat" small color="primary" :to="`/clients/${userId}?cc=1`">
            Create Coach Chat
          </v-btn>
        </div>
        <div v-for="chat in chatsForUser" :key="chat.id">
          <chat-listing-card :chat="chat" :userId="userId" class="mt-2 mb-8" />
        </div>
      </template>
      <v-dialog v-model="showModal" persistent class="limited-height" width="900" transition="dialog-bottom-transition">
        <task-form :userId="userId" @close="closeModal" />
      </v-dialog>
    </v-col>
    <v-col v-if="selectedEvent" cols="6" class="fill-height overflow-auto">
      <task-details :task="selectedEvent" />
    </v-col>
  </v-row>
</template>

<script>
import { extractNodes } from "@/lib/array";
import { TASK_EVENT_QUERY } from "@/graphql/queries/task";
import { CHATS_FOR_USER } from "@/graphql/queries/chat";
import { STAFF_QUERY } from "@/graphql/queries/user";
import { decodeId } from "@/lib/string";
import { getUserName } from "@/lib/username";
import { fancyShortDate } from "@/lib/fancy_date";
import ChatListingCard from "@/components/chats/ChatListingCard";
import TaskForm from "@/components/task/components/TaskForm";
import TaskDetails from "@/components/task/components/TaskDetails";
import TaskEventListingCard from "@/components/task/components/TaskEventListingCard";

export default {
  name: "UserTasks",
  components: {
    ChatListingCard,
    TaskForm,
    TaskDetails,
    TaskEventListingCard,
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
    assignedToId: {
      type: String,
      required: true,
    },
    showCompleted: {
      type: Boolean,
      default: false,
    },
    showAllUserTasks: {
      type: Boolean,
      default: true,
    },
    showSnoozed: {
      type: Boolean,
      required: false,
    },
    types: {
      type: Array,
      required: false,
    },
  },
  data() {
    return {
      taskEvents: [],
      selectedEvent: null,
      staff: [],
      showModal: null,
      hasCoachingChat: false,
    };
  },
  computed: {
    userTasks() {
      var tasks = [];
      if (this.taskEvents.edges) {
        tasks = this.taskEvents.edges;
        if (this.showAllUserTasks == false) {
          if (this.showCompleted == false) {
            tasks = tasks.filter((task) => task.node.stage.isResolved == false);
          }
          if (this.showSnoozed == false) {
            tasks = tasks.filter(
              (task) => task.node.snoozeUntil == null || task.node.snoozeUntil <= this.$moment().format("YYYY-MM-DD")
            );
          }
        }
      }

      return tasks;
    },
  },
  methods: {
    closeModal() {
      this.showModal = false;
    },
    selectTask(event) {
      this.selectedEvent = event;
      this.$emit("select", event);
    },
    fancyShortDate(date) {
      return fancyShortDate(date);
    },
  },
  apollo: {
    taskEvents: {
      query: TASK_EVENT_QUERY,
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.userId);
        let variables = {
          userId: encoded_json_payload,
          taskType_Id_In: this.types,
        };

        if (!this.showAllUserTasks) {
          variables["assignedToId"] = btoa("UserNode:" + this.assignedToId);
        }

        return variables;
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && response.data.taskEvents.edges) {
          var tasks = response.data.taskEvents.edges;
          if (this.showAllUserTasks == false) {
            if (this.showCompleted == false) {
              tasks = tasks.filter((task) => task.node.stage.isResolved == false);
            }
            if (this.showSnoozed == false) {
              tasks = tasks.filter((task) => task.node.snoozeUntil == null);
            }
          }
          if (tasks.length > 0) {
            if (this.selectedEvent) {
              var selected = tasks.find((evt) => evt.node.id == this.selectedEvent.id);
              if (selected) {
                this.selectedEvent = selected.node;
              } else {
                this.selectedEvent = tasks[0].node;
              }
            } else {
              this.selectedEvent = tasks[0].node;
            }
          } else {
            this.selectedEvent = null;
            this.$emit("no-task");
          }
        }
      },
    },
    users: {
      query: STAFF_QUERY,
      variables() {
        return {
          isActive: true,
          isStaff: true,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.staff = [];

          response.data.users.edges.forEach((item) => {
            let data = {
              id: decodeId(item.node.id),
              name: getUserName(item.node),
              avatar: item.node.userProfile?.profilePicture,
            };

            this.staff.push(data);
          });

          this.staff.sort((a, b) => (a.text > b.text ? 1 : -1));
        }
      },
    },
    chatsForUser: {
      query: CHATS_FOR_USER,
      variables() {
        return {
          userId: this.userId,
        };
      },
      fetchPolicy: "cache-and-network",
      update(data) {
        const chats = extractNodes(data.chatsForUser.edges);
        this.hasCoachingChat = chats.filter((chat) => chat.type == "COACH").length > 0;
        return chats.filter((chat) => chat.type !== "ACCOUNTABILITY");
      },
    },
  },
};
</script>
